/* ================================================================
   LFJ JTV ROOM — Checkpoint 43 (JTV / JTV Recap Series split)
   ----------------------------------------------------------------
   State machine for the JTV sub-navigation shell:

     'select'   — channel picker: JTV (mini-series) / JTV Recap Series
     'episodes' — JTV mini-series episode list (from videos.json)
     'volumes'  — JTV Recap Series volume picker (Vol. 1 / Vol. 2)
     'player'   — custom-controlled YouTube playback (shared by both)

   State is held in-memory only (no localStorage, no URL hash — the
   Menu back-to-menu link always exits this page entirely, so session
   continuity here isn't load-bearing). YouTube playback is driven via
   assets/lf-youtube-embed.js (custom controls, native YT chrome fully
   hidden), mounted on player entry and destroyed on player exit.

   IDs read via getElementById are load-bearing contracts with the
   markup in templates/page.jtv.liquid — don't rename one here without
   updating the template, and vice versa.

   Engine pattern: safeRun wrapper + __lfjJtvBound init-guard,
   matching every other engine in this theme.
================================================================ */

(function () {
  if (window.__lfjJtvBound) return;
  window.__lfjJtvBound = true;

  function safeRun(label, fn) {
    try { fn(); }
    catch (e) { console.warn('[LFJ:jtv] ' + label, e); }
  }

  document.addEventListener('DOMContentLoaded', function () {
    safeRun('init', function () {

      /* ── DOM refs ─────────────────────────────────────────────── */
      var screens = {
        select:   document.getElementById('jtv-screen-select'),
        episodes: document.getElementById('jtv-screen-episodes'),
        volumes:  document.getElementById('jtv-screen-volumes'),
        player:   document.getElementById('jtv-screen-player')
      };

      /* Channel select buttons */
      var btnSeries  = document.getElementById('jtv-btn-series');
      var btnRecap   = document.getElementById('jtv-btn-recap');

      /* Volume picker buttons */
      var btnVol1    = document.getElementById('jtv-btn-vol1');
      var btnVol2    = document.getElementById('jtv-btn-vol2');

      /* Episodes grid (populated from videos.json) */
      var episodesGrid  = document.getElementById('jtv-episodes-grid');
      var episodesEmpty = document.getElementById('jtv-episodes-empty');

      /* Player elements */
      var playerMount    = document.getElementById('jtv-player-mount');
      var playerEmpty    = null; /* LFJ_THEATER_PLAYER renders its own "NO SIGNAL" state now — see enterPlayerWith */
      var playerTitle    = document.getElementById('jtv-player-title-text');
      var playerVolLabel = document.getElementById('jtv-player-vol-label');
      var activeEmbed    = null; /* current LFJ_THEATER_PLAYER mount, if any (see assets/lfj-jtv-player.js + lfj-yt-mask.js) */

      /* Back buttons — one per screen that needs one */
      var backBtns = document.querySelectorAll('.jtv-back');

      /* ── State ──────────────────────────────────── */
      var currentState  = 'select';
      /* Tracks which screen "player" was entered from, so back
         returns to the right place — 'volumes' or 'episodes'. */
      var playerEntry   = 'volumes';

      /* Video data injected from settings via data attributes on
         the player screen element — avoids re-reading the DOM on
         every state transition. jtv_vol1_id/vol2_id are YouTube IDs. */
      var playerEl = screens.player;
      var videoData = {
        1: {
          youtubeId: (playerEl && playerEl.dataset.vol1Id)    || '',
          title:     (playerEl && playerEl.dataset.vol1Title) || 'JTV RECAP SERIES \u2014 VOL. 1'
        },
        2: {
          youtubeId: (playerEl && playerEl.dataset.vol2Id)    || '',
          title:     (playerEl && playerEl.dataset.vol2Title) || 'JTV RECAP SERIES \u2014 VOL. 2'
        }
      };

      /* ── Screen transition ────────────────────────────── */
      function goTo(state) {
        /* On leaving player: tear down the engine mount to stop playback */
        if (currentState === 'player' && state !== 'player') {
          if (activeEmbed && window.LFJ_THEATER_PLAYER) {
            window.LFJ_THEATER_PLAYER.unmount(playerMount);
            activeEmbed = null;
          }
        }

        /* Hide all screens */
        Object.values(screens).forEach(function (el) {
          if (el) el.classList.remove('jtv-screen--active');
        });

        /* Show target */
        if (screens[state]) screens[state].classList.add('jtv-screen--active');
        currentState = state;
      }

      /* ── Enter player with an entry shaped for LFJ_THEATER_PLAYER:
         { websiteVideoUrl, youtubeId, igUrl, title } — any subset.
         The engine itself renders its own "NO SIGNAL" state when
         none of the three sources are present, and builds a
         website/YouTube toggle automatically when both exist. ─── */
      function enterPlayerWith(entry, entryScreen) {
        playerEntry = entryScreen;

        if (playerVolLabel) playerVolLabel.textContent = entry.title || '';
        if (playerTitle) playerTitle.textContent = entry.title || '';

        if (playerMount && window.LFJ_THEATER_PLAYER) {
          window.LFJ_THEATER_PLAYER.mount(playerMount, entry);
          activeEmbed = true;
        }

        goTo('player');
      }

      function enterPlayer(vol) {
        enterPlayerWith(videoData[vol], 'volumes');
      }

      /* ── Episodes grid ─ built from assets/videos.json, not
         hardcoded settings, so new episodes need no template edit.
         Sniffs videoUrl for a YouTube link vs. a real hosted file
         (website wins by default in the engine when both exist),
         and passes igUrl straight through for Instagram-only clips. ── */
      /* Checkpoint 61 — JTV dynamic sub-hub (v3 §4e): episodes now group
         under their jtv_subtype (Recap/Origins/Live/Radio/Two/News) and
         a subtype section only renders when it has at least one real
         entry — never an empty "JTV Origins" heading for an unused
         option. Entries with no jtv_subtype (or one that doesn't match
         a canonical value — e.g. the existing real episode, which
         predates this field and carries jtv_series:"episode") fall into
         a plain "EPISODES" group so nothing already-real disappears. */
      var JTV_SUBTYPES = [
        { key: 'recap',   label: 'JTV RECAP' },
        { key: 'origins', label: 'JTV ORIGINS' },
        { key: 'live',    label: 'JTV LIVE' },
        { key: 'radio',   label: 'JTV RADIO' },
        { key: 'two',     label: 'JTV TWO' },
        { key: 'news',    label: 'JTV NEWS' }
      ];

      /* Checkpoint 65 — the Discord bot writes the show as `jtv_series`
         (recap|origins|live|radio|two|news); this sub-hub was reading
         `jtv_subtype`, so nothing the bot uploaded would ever group.
         Accept both; the legacy real episode's jtv_series:"episode" isn't
         canonical, so it still falls into the plain EPISODES group. */
      function subtypeOf(ep) { return ep.jtv_subtype || ep.jtv_series || ''; }

      function buildEpisodeBtn(ep) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'jtv-vol-btn';
        var epUrl = ep.videoUrl || ep.src || '';
        var isYouTube = /youtu\.?be/.test(epUrl);
        var idMatch = isYouTube ? epUrl.match(/(?:youtu\.be\/|v=)([\w-]+)/) : null;
        var entry = {
          title: ep.title || 'JTV EPISODE',
          youtubeId: idMatch ? idMatch[1] : '',
          websiteVideoUrl: (!isYouTube && epUrl) ? epUrl : '',
          igUrl: ep.igUrl || ''
        };
        /* Checkpoint 65 — the Discord bot never writes jtv_episode_number, so
           every uploaded episode used to read "EP. ?". Use the number when
           there is one, otherwise the year (the bot always writes it).
           Built with textContent: a title from JSON can't inject HTML. */
        var lab = document.createElement('span');
        lab.className = 'jtv-vol-label';
        lab.textContent = ep.jtv_episode_number ? 'EP. ' + ep.jtv_episode_number : (ep.year ? String(ep.year) : 'NEW');
        var sub = document.createElement('span');
        sub.className = 'jtv-vol-sublabel';
        sub.textContent = (ep.title || 'JTV EPISODE').toUpperCase();
        btn.appendChild(lab);
        btn.appendChild(sub);
        btn.addEventListener('click', function () {
          safeRun('episode-btn', function () { enterPlayerWith(entry, 'episodes'); });
        });
        return btn;
      }

      function renderEpisodes(list) {
        var episodes = (list || []).filter(function (v) {
          var legacy = v.category === 'jtv' && v.type === 'JTV Episode';
          var byDestination = Array.isArray(v.destination) && v.destination.indexOf('jtv') > -1;
          return legacy || byDestination;
        }).sort(function (a, b) {
          /* jtv_episode_number is the real deliberate ordering signal;
             fall back to sort_index for anything that omits an episode
             number, so uploaded episodes land in a sane position
             instead of all collapsing to "?". */
          var aHasEp = typeof a.jtv_episode_number === 'number';
          var bHasEp = typeof b.jtv_episode_number === 'number';
          if (aHasEp && bHasEp) return a.jtv_episode_number - b.jtv_episode_number;
          if (aHasEp) return -1;
          if (bHasEp) return 1;
          var ai = typeof a.sort_index === 'number' ? a.sort_index : Infinity;
          var bi = typeof b.sort_index === 'number' ? b.sort_index : Infinity;
          return ai - bi;
        });

        if (!episodes.length) {
          if (episodesEmpty) episodesEmpty.hidden = false;
          return;
        }
        if (episodesEmpty) episodesEmpty.hidden = true;

        var canonicalKeys = JTV_SUBTYPES.map(function (s) { return s.key; });
        var fallback = episodes.filter(function (ep) { return canonicalKeys.indexOf(subtypeOf(ep)) === -1; });

        function renderGroup(label, items) {
          if (!items.length) return; /* the actual fix: no items → no heading, no empty section */
          var heading = document.createElement('div');
          heading.className = 'jtv-subhub-heading';
          heading.textContent = label;
          episodesGrid.insertBefore(heading, episodesEmpty);
          items.forEach(function (ep) { episodesGrid.insertBefore(buildEpisodeBtn(ep), episodesEmpty); });
        }

        JTV_SUBTYPES.forEach(function (s) {
          renderGroup(s.label, episodes.filter(function (ep) { return subtypeOf(ep) === s.key; }));
        });
        /* Plain "EPISODES" heading only makes sense once real subtypes
           exist alongside it — with just the one legacy entry today,
           skip the heading and render it exactly as before. */
        if (fallback.length) {
          var hasAnyCanonical = JTV_SUBTYPES.some(function (s) {
            return episodes.some(function (ep) { return subtypeOf(ep) === s.key; });
          });
          if (hasAnyCanonical) renderGroup('EPISODES', fallback);
          else fallback.forEach(function (ep) { episodesGrid.insertBefore(buildEpisodeBtn(ep), episodesEmpty); });
        }
      }

      if (window.LF_VIDEOS_READY) {
        window.LF_VIDEOS_READY
          .then(function () { renderEpisodes(window.LF_CONFIG.videos); })
          .catch(function () { if (episodesEmpty) episodesEmpty.hidden = false; });
      }

      /* ── Back button routing ────────────────────────── */
      function handleBack() {
        safeRun('back', function () {
          switch (currentState) {
            case 'volumes':  goTo('select');     break;
            case 'episodes': goTo('select');     break;
            case 'player':   goTo(playerEntry);  break;
            default:         goTo('select');     break;
          }
        });
      }

      /* ── Wire channel select ────────────────────────── */
      if (btnSeries) {
        btnSeries.addEventListener('click', function () {
          safeRun('series', function () { goTo('episodes'); });
        });
      }
      if (btnRecap) {
        btnRecap.addEventListener('click', function () {
          safeRun('recap', function () { goTo('volumes'); });
        });
      }

      /* ── Wire volume picker ────────────────────────── */
      if (btnVol1) {
        btnVol1.addEventListener('click', function () {
          safeRun('vol1', function () { enterPlayer(1); });
        });
      }
      if (btnVol2) {
        btnVol2.addEventListener('click', function () {
          safeRun('vol2', function () { enterPlayer(2); });
        });
      }

      /* ── Wire back buttons ────────────────────────────────────── */
      backBtns.forEach(function (btn) {
        btn.addEventListener('click', handleBack);
      });

      /* ── Keyboard: Escape = back, also exits player ───────────── */
      document.addEventListener('keydown', function (e) {
        safeRun('keydown', function () {
          if (e.key === 'Escape' && currentState !== 'select') {
            handleBack();
          }
        });
      });

      /* ── Initial state: channel select ───────────────────────── */
      goTo('select');

    }); /* end safeRun init */
  }); /* end DOMContentLoaded */

}());
